import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot,} from '@angular/router';
import { LoginType, RefreshTokenRequest, TokensClient } from "@kodit/core/data-api";
import {stringIsNullOrEmpty} from '@kodit/core/shared';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _client: TokensClient
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    const redirectToLogin = () =>
      this._router.navigate(['/autentikacija/prijava'], {
        queryParams: { returnUrl: state.url },
      });

    try {
      if (!this._authService.getToken || !this._authService.getRefreshToken) {
        redirectToLogin();
        return false;
      }

      const ensureValidToken$: Observable<boolean> = this._authService.isUserAuthenticated()
        ? of(true)
        : this._authService.refreshTokens().pipe(map((token) => !!token));

      return ensureValidToken$.pipe(
        map((authenticated) => {
          if (!authenticated) {
            redirectToLogin();
            return false;
          }

          const packageName: string[] = next.data['packageName'];
          if (packageName && !this._authService.hasPackage(packageName)) {
            this._router.navigate(['/access-denied'], { queryParams: { reason: 'paket' } });
            return false;
          }

          const claimName: string = next.data['claimName'];
          if (claimName && !this._authService.hasClaim(claimName)) {
            this._router.navigate(['/access-denied'], { queryParams: { reason: 'permisija' } });
            return false;
          }

          return true;
        }),
        catchError(() => {
          redirectToLogin();
          return of(false);
        }),
      );
    } catch {
      redirectToLogin();
      return false;
    }
  }

  // stara verzija ali nam puca dependency iz nekog razloga ako obrisemo ovu funkciju ?!
  public async tryRefreshingTokens(): Promise<boolean> {
    // Try refreshing tokens using refresh token
    const refreshToken: string = this._authService.getRefreshToken;
    const token: string = this._authService.getToken;
    const sessionId: string = this._authService.getSessionId;

    if (!token || !refreshToken) {
      return false;
    }

    let isRefreshSuccess: boolean;
    const currentTenant = this._authService.getCurrentTenant;

    try {
      const response = this._client
        .refresh(
          new RefreshTokenRequest({
            refreshToken: refreshToken,
            token: token,
            sessionId: sessionId,
            loginType : LoginType.WEB
          }),
          currentTenant
        )
        .toPromise();

      if (stringIsNullOrEmpty((await response).data.sessionId)) {
       // this._openIzlogujUpozorenjeDialog();
        return;
      }

      // If token refresh is successful, set new tokens in local storage.
      const newToken = (await response).data.token;
      const newRefreshToken = (await response).data.refreshToken;
      const newSessionId = (await response).data.sessionId;
      this._authService.setTokens(newToken, newRefreshToken, newSessionId);
      isRefreshSuccess = true;
    } catch (ex) {
      isRefreshSuccess = false;
      this._authService.logout();
    }

    return isRefreshSuccess;

  }
}
